import RevisionTable from 'components/tables/data/revision/RevisionTable';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDepartmentUiMode from 'model/useDepartmentUiMode';

/**
 * Page used to display revisions.
 *
 */
export default function RevisionsPage({ kind }: { kind: 'revision' | 'form' }) {
  const { mode: departmentUiMode } = useDepartmentUiMode();
  const [searchParams, setSearchParams] = useSearchParams();

  const [initialFilters] = useState(() => {
    const filters: Record<string, any> = {};

    if (searchParams.has('organizationId')) {
      filters['organization.organizationId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('organizationId'),
      };
    }

    if (searchParams.has('departmentId')) {
      filters['department.departmentId'] = {
        mode: departmentUiMode === 'legacy' ? 'exact' : 'subtree',
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('departmentId'),
      };
    }

    if (searchParams.has('deviceTypeId')) {
      filters['deviceType.deviceTypeId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('deviceTypeId'),
      };
    }

    if (searchParams.has('deviceSubtypeId')) {
      filters['deviceSubtypes.deviceTypeId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('deviceSubtypeId'),
      };
    }

    if (searchParams.has('deviceId')) {
      filters['_search.deviceId'] = {
        filterType: 'text',
        type: 'contains',
        filter: searchParams.get('deviceId'),
      };
    }

    if (searchParams.has('assignedBy')) {
      filters['assignedBy.organizationId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('assignedBy'),
      };
    }

    if (searchParams.has('technician')) {
      filters['technician.userId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('technician'),
      };
    }

    if (searchParams.has('deadlineStatus')) {
      filters['deadlineStatus'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('deadlineStatus'),
      };
    }

    if (searchParams.has('safetyPoint')) {
      filters['safetyPoint'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('safetyPoint'),
      };
    }

    if (searchParams.has('revisionStatusId')) {
      filters['revisionStatus.revisionStatusId'] = {
        filter: searchParams.get('revisionStatusId')!.split(',').map(Number).filter(Boolean),
      };
    }

    if (searchParams.has('remoteSourceId')) {
      filters['remoteSource.remoteSourceId'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('remoteSourceId'),
      };
    }

    if (searchParams.has('revisionTemplateSlug')) {
      filters['revisionTemplate.slug'] = {
        filterType: 'text',
        type: 'equals',
        filter: searchParams.get('revisionTemplateSlug'),
      };
    }

    return filters;
  });

  useEffect(() => {
    setSearchParams(
      (params) => {
        params.delete('organizationId');
        params.delete('departmentId');
        params.delete('deviceTypeId');
        params.delete('deviceSubtypeId');
        params.delete('deviceId');
        params.delete('assignedBy');
        params.delete('technician');
        params.delete('deadlineStatus');
        params.delete('safetyPoint');
        params.delete('revisionStatusId');
        params.delete('remoteSourceId');
        params.delete('revisionTemplateSlug');
        return params;
      },
      { replace: true }
    );
  }, []);

  return (
    <DashboardLayout
      key={kind}
      height="screen"
      breadcrumbs={[{ title: kind === 'revision' ? 'Revízne správy' : 'Formuláre' }]}
    >
      <RevisionTable initialFilters={initialFilters} kind={kind} />
    </DashboardLayout>
  );
}
